import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@common/react-query/query-keys';
import { QueryTag } from '@common/ts/enums';
import axiosInstanceAnalyze from '@/axios/AxiosInstanceAnalyze';
import { API_ENDPOINTS } from '@/api-endpoints';

export interface ComparisonResult {
  filter_a_name: string;
  filter_b_name: string;
  shared_themes: Array<{ title: string; body: string }>;
  unique_to_a: Array<{ title: string; body: string }>;
  unique_to_b: Array<{ title: string; body: string }>;
  statistical_comparison: Array<{
    /**
     * Stable identifier for localization. Optional because comparisons
     * persisted before this field was added still need to render — those
     * older rows fall back to the human-readable `metric` field below.
     */
    metric_key?: string;
    metric: string;
    filter_a_value: number;
    filter_b_value: number;
    delta: string;
  }>;
  narrative: string;
  per_question?: Array<{
    question_id: string;
    question_text: string;
    key_differences: Array<{ title: string; body: string }>;
    shared_patterns: Array<{ title: string; body: string }>;
    summary_a: string;
    summary_b: string;
  }>;
  sample_profile_a?: Record<string, Array<{ value: string; count: number; percentage: number }>>;
  sample_profile_b?: Record<string, Array<{ value: string; count: number; percentage: number }>>;
  warnings?: string[];
}

export interface ComparisonListItem {
  _id: string;
  filter_id_a: string;
  filter_id_b: string;
  filter_a_name: string;
  filter_b_name: string;
  sample_size_a: number;
  sample_size_b: number;
  is_stale: boolean;
  createdAt: string;
}

interface ComparisonStartResponse {
  comparison_id: string;
  status: string;
  result?: ComparisonResult;
}

interface ComparisonStatusResponse {
  status: string;
  progress?: string;
  result?: ComparisonResult;
  comparison_id?: string;
  filter_id_a?: string;
  filter_id_b?: string;
}

export interface ComparisonWithFilters {
  result: ComparisonResult;
  filter_id_a?: string;
  filter_id_b?: string;
}

const POLL_INTERVAL = 3000;
const MAX_POLL_TIME = 300000; // 5 minutes

export function useCompareSegments() {
  let abortController: AbortController | null = null;

  const mutation = useMutation({
    mutationFn: async ({
      projectId,
      filterIdA,
      filterIdB,
      onProgress,
    }: {
      projectId: string;
      filterIdA: string;
      filterIdB: string;
      onProgress?: (progress: string) => void;
    }) => {
      abortController = new AbortController();
      const { signal } = abortController;

      // Start the comparison — returns immediately with job ID or cached result
      const { data: startResponse } = await axiosInstanceAnalyze.post<ComparisonStartResponse>(
        API_ENDPOINTS.COMPARISON.START(projectId),
        {
          filter_id_a: filterIdA,
          filter_id_b: filterIdB,
        },
        { signal },
      );

      // If cached result returned immediately, we're done
      if (startResponse.status === 'done' && startResponse.result) {
        return { comparisonId: startResponse.comparison_id, result: startResponse.result };
      }

      // Poll for completion
      const comparisonId = startResponse.comparison_id;
      const startTime = Date.now();
      onProgress?.('Loading segment data...');

      while (Date.now() - startTime < MAX_POLL_TIME) {
        signal.throwIfAborted();
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
        signal.throwIfAborted();

        const { data: statusResponse } = await axiosInstanceAnalyze.get<ComparisonStatusResponse>(
          API_ENDPOINTS.COMPARISON.STATUS(projectId, comparisonId),
          { signal },
        );

        if (statusResponse.progress) {
          onProgress?.(statusResponse.progress);
        }

        if (statusResponse.status === 'done' && statusResponse.result) {
          return {
            comparisonId: statusResponse.comparison_id || comparisonId,
            result: statusResponse.result,
          };
        }

        if (statusResponse.status === 'error') {
          throw new Error(statusResponse.progress || 'Comparison failed');
        }
      }

      throw new Error('Comparison timed out. Please try again.');
    },
  });

  return {
    ...mutation,
    abort: () => abortController?.abort(),
  };
}

export function useListComparisons(projectId?: string) {
  return useQuery({
    queryKey: QUERY_KEYS.COMPARISONS(projectId),
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get<ComparisonListItem[]>(
        API_ENDPOINTS.COMPARISON.LIST(projectId!),
      );
      return data;
    },
    enabled: !!projectId,
  });
}

export function useGetComparison(projectId?: string, comparisonId?: string | null) {
  return useQuery<ComparisonWithFilters>({
    queryKey: ['comparison', { projectId, comparisonId }],
    queryFn: async () => {
      const { data } = await axiosInstanceAnalyze.get<ComparisonStatusResponse>(
        API_ENDPOINTS.COMPARISON.STATUS(projectId!, comparisonId!),
      );
      if (data.status === 'done' && data.result) {
        return {
          result: data.result,
          filter_id_a: data.filter_id_a,
          filter_id_b: data.filter_id_b,
        };
      }
      throw new Error('Comparison not found');
    },
    enabled: !!projectId && !!comparisonId,
    retry: 1,
  });
}

export function useDeleteComparison() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      comparisonId,
    }: {
      projectId: string;
      comparisonId: string;
    }) => {
      await axiosInstanceAnalyze.delete(API_ENDPOINTS.COMPARISON.DELETE(projectId, comparisonId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryTag.COMPARISONS] });
    },
  });
}
